/**
 * Integrations Settings (DC-1157)
 *
 * Unified integration connections management for Databricks, Collibra, Veeva, and future integrations.
 *
 * Features:
 * - List available integration types
 * - Create/edit/delete connections
 * - Test connections
 * - View sync job history (for sync-enabled integrations)
 *
 * @see Documentation/Development/Design Docs/Sprint 13/Unified_Integration_Architecture.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BRANDING } from '../../config/branding';
import { useAuth } from '../../hooks/useAuth';
import { DatabricksConnectionForm } from '../../components/integrations/DatabricksConnectionForm';
import { DatabricksTableBrowser } from '../../components/integrations/DatabricksTableBrowser';
import {
  DatabricksBulkAnalyzeProgress,
  type BulkAnalyzeTable,
} from '../../components/integrations/DatabricksBulkAnalyzeProgress';
import './Integrations.css';

// ============================================================================
// TYPES
// ============================================================================

interface IntegrationInfo {
  id: string;
  name: string;
  category: string;
  description: string;
  capabilities: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  authMethods: string[];
}

interface ConnectionResponse {
  id: string;
  integrationType: string;
  name: string;
  description?: string;
  endpointUrl: string;
  authType: string;
  status: 'pending' | 'active' | 'inactive' | 'error' | 'testing';
  lastTestAt?: string;
  lastError?: string;
  avgLatencyMs?: number;
  createdAt: string;
  updatedAt: string;
}

interface RegistryStatus {
  initialized: boolean;
  providerCount: number;
  readyCount: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const Integrations: React.FC = () => {
  const { accessToken: token } = useAuth();

  // State
  const [integrations, setIntegrations] = useState<IntegrationInfo[]>([]);
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [registryStatus, setRegistryStatus] = useState<RegistryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationInfo | null>(null);

  // Success/error toast
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Catalog browser
  const [exploringConnection, setExploringConnection] = useState<ConnectionResponse | null>(null);

  // Bulk Analyze state for the catalog browser. multiSelectMode toggles
  // checkbox UI; bulkSelection accumulates the chosen tables; running
  // bulk batch is captured in bulkBatch which (when non-null) renders
  // the progress modal.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [bulkSelection, setBulkSelection] = useState<BulkAnalyzeTable[]>([]);
  const [bulkBatch, setBulkBatch] = useState<{
    connectionId: string;
    tables: BulkAnalyzeTable[];
  } | null>(null);

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const fetchData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch available integrations
      const integrationsRes = await fetch('/api/integrations', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!integrationsRes.ok) {
        throw new Error('Failed to fetch integrations');
      }

      const integrationsData = await integrationsRes.json();
      setIntegrations(integrationsData.integrations || []);
      setRegistryStatus(integrationsData.registry);

      // Fetch connections
      const connectionsRes = await fetch('/api/integrations/connections', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!connectionsRes.ok) {
        throw new Error('Failed to fetch connections');
      }

      const connectionsData = await connectionsRes.json();
      setConnections(connectionsData.connections || []);
    } catch (err) {
      console.error('Failed to fetch integration data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-dismiss action message after 5 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const handleTestConnection = async (connectionId: string) => {
    if (!token) return;

    try {
      const res = await fetch(`/api/integrations/connections/${connectionId}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        const errMsg = typeof data.error === 'string'
          ? data.error
          : data.error?.message || 'Test failed';
        throw new Error(errMsg);
      }

      // Refresh connections to get updated status
      await fetchData();
      setActionMessage({ type: 'success', text: 'Connection test completed successfully' });
    } catch (err) {
      console.error('Connection test failed:', err);
      setError(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!token) return;
    if (!confirm('Are you sure you want to delete this connection?')) return;

    try {
      const res = await fetch(`/api/integrations/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to delete connection');
      }

      // Close browser if the deleted connection was being explored
      if (exploringConnection?.id === connectionId) {
        setExploringConnection(null);
      }

      // Refresh connections
      await fetchData();
      setActionMessage({ type: 'success', text: 'Connection deleted successfully' });
    } catch (err) {
      console.error('Failed to delete connection:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete connection');
    }
  };

  const handleCreateConnection = async (formData: {
    name: string;
    description: string;
    endpointUrl: string;
    authType: 'pat' | 'oauth2';
    token?: string;
    clientId?: string;
    clientSecret?: string;
    connectionConfig: object;
  }) => {
    if (!token || !selectedIntegration) return;

    try {
      const payload = {
        integrationType: selectedIntegration.id,
        name: formData.name,
        description: formData.description,
        endpointUrl: formData.endpointUrl,
        authType: formData.authType,
        credentials: {
          authType: formData.authType,
          ...(formData.authType === 'pat'
            ? { token: formData.token }
            : { clientId: formData.clientId, clientSecret: formData.clientSecret }),
        },
        connectionConfig: formData.connectionConfig,
      };

      const res = await fetch('/api/integrations/connections', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        const errMsg = typeof data.error === 'string'
          ? data.error
          : data.error?.message || 'Failed to create connection';
        throw new Error(errMsg);
      }

      // Close modal and refresh
      setShowAddModal(false);
      setSelectedIntegration(null);
      await fetchData();
      setActionMessage({ type: 'success', text: `Connection '${formData.name}' created successfully` });
    } catch (err) {
      console.error('Failed to create connection:', err);
      throw err; // Re-throw so the form can display the error
    }
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  const STATUS_BADGE_CLASSES: Record<string, string> = {
    active: 'status-badge status-active',
    error: 'status-badge status-error',
    testing: 'status-badge status-testing',
    inactive: 'status-badge status-inactive',
  };

  const getStatusBadgeClass = (status: ConnectionResponse['status']): string => {
    return STATUS_BADGE_CLASSES[status] ?? 'status-badge status-pending';
  };

  const CATEGORY_ICONS: Record<string, string> = {
    data_platform: '\uD83D\uDCBE',
    data_catalog: '\uD83D\uDCDA',
    life_sciences: '\uD83E\uDDEC',
    cloud_storage: '\u2601\uFE0F',
  };

  const getCategoryIcon = (category: string): string => {
    return CATEGORY_ICONS[category] ?? '\uD83D\uDD17';
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (loading) {
    return (
      <div className="integrations-page">
        <div className="integrations-loading">
          <div className="loading-spinner" />
          <p>Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="integrations-page">
      {/* Header */}
      <div className="integrations-header">
        <div className="header-content">
          <h2>Integrations</h2>
          <p className="header-subtitle">
            {`Connect ${BRANDING.productName} to external data platforms, catalogs, and life sciences systems`}
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            disabled={integrations.length === 0}
          >
            + Add Connection
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Action Message Toast */}
      {actionMessage && (
        <div className={`intg-action-message ${actionMessage.type}`}>
          <span>{actionMessage.text}</span>
          <button onClick={() => setActionMessage(null)}>&times;</button>
        </div>
      )}

      {/* Registry Status */}
      {registryStatus && (
        <div className="registry-status">
          <span className={`status-indicator ${registryStatus.initialized ? 'initialized' : 'not-initialized'}`} />
          <span>
            Integration Framework: {registryStatus.readyCount}/{registryStatus.providerCount} providers ready
          </span>
        </div>
      )}

      {/* Available Integrations */}
      <section className="available-integrations">
        <h3>Available Integrations</h3>
        {integrations.length === 0 ? (
          <div className="empty-state">
            <p>No integration providers are currently available.</p>
            <p className="hint">Integration providers will be registered when the application starts.</p>
          </div>
        ) : (
          <div className="integration-cards">
            {integrations.map((integration) => (
              <div key={integration.id} className="integration-card">
                <div className="card-icon">{getCategoryIcon(integration.category)}</div>
                <div className="card-content">
                  <h4>{integration.name}</h4>
                  <p className="card-description">{integration.description}</p>
                  <div className="card-capabilities">
                    {integration.capabilities.slice(0, 3).map((cap) => (
                      <span key={cap.id} className="capability-tag">
                        {cap.name}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setSelectedIntegration(integration);
                    setShowAddModal(true);
                  }}
                >
                  Connect
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Active Connections */}
      <section className="active-connections">
        <h3>Active Connections</h3>
        {connections.length === 0 ? (
          <div className="empty-state">
            <p>No connections configured yet.</p>
            <p className="hint">Add a connection above to get started.</p>
          </div>
        ) : (
          <table className="connections-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Endpoint</th>
                <th>Status</th>
                <th>Last Tested</th>
                <th>Latency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {connections.map((connection) => (
                <tr key={connection.id}>
                  <td>
                    <strong>{connection.name}</strong>
                    {connection.description && (
                      <span className="description">{connection.description}</span>
                    )}
                  </td>
                  <td>
                    <span className="type-badge">{connection.integrationType}</span>
                  </td>
                  <td className="endpoint-cell">
                    <code>{connection.endpointUrl}</code>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(connection.status)}>
                      {connection.status}
                    </span>
                    {connection.lastError && (
                      <span
                        className="error-hint"
                        role="img"
                        aria-label={`Connection error: ${connection.lastError}`}
                        title={connection.lastError}
                      >
                        ⚠️
                      </span>
                    )}
                  </td>
                  <td>
                    {connection.lastTestAt ? formatDate(connection.lastTestAt) : 'Never'}
                  </td>
                  <td>
                    {connection.avgLatencyMs ? `${connection.avgLatencyMs}ms` : '-'}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-icon"
                      onClick={() => handleTestConnection(connection.id)}
                      title="Test Connection"
                      aria-label="Test connection"
                    >
                      🔄
                    </button>
                    {connection.integrationType === 'databricks' && connection.status === 'active' && (
                      <button
                        className={`btn-icon${exploringConnection?.id === connection.id ? ' btn-active' : ''}`}
                        onClick={() =>
                          setExploringConnection(
                            exploringConnection?.id === connection.id ? null : connection
                          )
                        }
                        title="Explore Catalog"
                        aria-label="Explore catalog"
                      >
                        🔍
                      </button>
                    )}
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteConnection(connection.id)}
                      title="Delete Connection"
                      aria-label="Delete connection"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Catalog Browser Panel */}
        {exploringConnection && (
          <div className="intg-browser-panel">
            <div className="intg-browser-header">
              <h4>Catalog Browser: {exploringConnection.name}</h4>
              <button
                className="intg-browser-close"
                onClick={() => setExploringConnection(null)}
                title="Close browser"
              >
                &times; Close
              </button>
            </div>
            <div
              className="intg-browser-body"
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '0.85rem',
                  padding: '6px 10px',
                  background: '#f8f9fa',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={multiSelectMode}
                    onChange={(e) => {
                      setMultiSelectMode(e.target.checked);
                      if (!e.target.checked) setBulkSelection([]);
                    }}
                  />
                  <span style={{ color: '#1a1a2e' }}>Multi-select tables for bulk FAIR analysis</span>
                </label>
                {multiSelectMode && bulkSelection.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setBulkBatch({
                        connectionId: exploringConnection.id,
                        tables: bulkSelection,
                      })
                    }
                    style={{
                      background: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 14px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    Run FAIR on selected ({bulkSelection.length})
                  </button>
                )}
              </div>
              <DatabricksTableBrowser
                connectionId={exploringConnection.id}
                onAnalyze={(table) => {
                  setActionMessage({
                    type: 'success',
                    text: `Analysis started for ${table.catalog}.${table.schema}.${table.table}`,
                  });
                }}
                showAnalyzeButton={!multiSelectMode}
                multiSelect={multiSelectMode}
                onSelectionChange={setBulkSelection}
              />
            </div>
          </div>
        )}
      </section>

      {/* Bulk Analyze progress modal */}
      {bulkBatch && (
        <DatabricksBulkAnalyzeProgress
          connectionId={bulkBatch.connectionId}
          tables={bulkBatch.tables}
          onClose={() => {
            setBulkBatch(null);
            setBulkSelection([]);
            setMultiSelectMode(false);
          }}
        />
      )}

      {/* Add Connection Modal */}
      {showAddModal && (
        <div className="intg-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="intg-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="intg-modal-header">
              <h3>
                {selectedIntegration
                  ? `Add ${selectedIntegration.name} Connection`
                  : 'Add Connection'}
              </h3>
              <button className="intg-modal-close" onClick={() => setShowAddModal(false)}>
                ×
              </button>
            </div>
            <div className="intg-modal-body">
              {integrations.length === 0 ? (
                <p>No integration providers available.</p>
              ) : selectedIntegration?.id === 'databricks' ? (
                <DatabricksConnectionForm
                  onSubmit={handleCreateConnection}
                  onCancel={() => {
                    setShowAddModal(false);
                    setSelectedIntegration(null);
                  }}
                />
              ) : selectedIntegration ? (
                <div className="connection-form-placeholder">
                  <div className="selected-integration-info">
                    <h4>{selectedIntegration.name}</h4>
                    <p>{selectedIntegration.description}</p>
                    <p>
                      <strong>Auth Methods:</strong>{' '}
                      {selectedIntegration.authMethods.join(', ')}
                    </p>
                  </div>
                  <p className="hint">
                    Connection form for {selectedIntegration.name} is coming soon.
                  </p>
                </div>
              ) : (
                <div className="integration-selector">
                  <p>Select an integration type to configure:</p>
                  <div className="integration-options">
                    {integrations.map((integration) => (
                      <button
                        key={integration.id}
                        className="integration-option"
                        onClick={() => setSelectedIntegration(integration)}
                      >
                        <span className="option-icon">{getCategoryIcon(integration.category)}</span>
                        <span className="option-name">{integration.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {selectedIntegration?.id !== 'databricks' && (
              <div className="intg-modal-footer">
                <button className="btn-secondary" onClick={() => {
                  setShowAddModal(false);
                  setSelectedIntegration(null);
                }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Integrations;
